import {
  Alert,
  MultiSelect,
  NumberInput,
  SimpleSelect,
  SimpleSelectOption,
  SwitchInput,
} from 'corso-admin-ui';
import { Pegasus } from 'corso-types';
import { Controller, useWatch } from 'react-hook-form';
import { useAutomationFormContext } from '~/features/settings/automations/hooks/useAutomationForm';
import { useProductGroups } from '~/features/settings/hooks/useProductGroups';
import { useIsStripeConnected } from '~/hooks/useIntegrationSettings';
import {
  advancedExchangeKindLabel,
  advancedExchangeLabel,
  advancedExchangeSortCriteriaLabel,
} from '~/utils/enumNameMaps';
import { getNullableNumberValue } from '~/utils/form';
import { useFactValueProductTags } from '../../hooks/useFactValues';

type ExchangeKind =
  Pegasus.EventOfferAdvancedExchange['params']['exchangeKind'];

const advancedExchangeDescription = {
  NO_PAYMENT_OR_REFUND:
    'Customers will not be charged or refunded for price differences between exchanged items.',
  PAYMENT:
    'Customers will pay the difference in cost when selecting a more expensive item. No refunds are issued if a lower-priced item is selected.',
  PAYMENT_AND_PARTIAL_REFUND:
    'Customers will pay or receive the difference in cost when selecting an item with a different price.',
} satisfies Record<Pegasus.AdvancedExchangeMethod, string>;

const exchangeKindDescription = {
  product:
    'Customers can exchange for products in the selected product groups or tags.',
  variant:
    'Customers can exchange for a different variant of the same product, such as another size or color.',
} satisfies Record<ExchangeKind, string>;

const exchangeKindOptions = [
  { label: advancedExchangeKindLabel.product, value: 'product' },
  { label: advancedExchangeKindLabel.variant, value: 'variant' },
] satisfies SimpleSelectOption<ExchangeKind>[];

export default function EventOfferAdvancedExchanges() {
  const { control, watch, setValue, getValues } = useAutomationFormContext();

  const { data } = useProductGroups();

  const groups = data.map((group) => ({
    label: group.name,
    value: `${group.id}`,
  }));

  const productTags = useFactValueProductTags();

  const productTagOptions = productTags.map((tag) => ({
    label: tag,
    value: tag,
  }));

  const selectedTags = watch('event.params.storeProductTags') ?? [];

  const isStripeConnected = useIsStripeConnected();

  const advancedExchangeOptions = Object.values(
    Pegasus.AdvancedExchangeMethod,
  ).map((v) => ({
    label: advancedExchangeLabel[v],
    value: v,
  }));

  const isFilterAndSearchEnabled = watch(
    'event.params.isAdvancedExchangeFilterAndSearchEnabled',
  );

  // useWatch, not watch: `setValue('event.params', ...)` doesn't notify a watcher on a child path
  const exchangeKind = useWatch({ control, name: 'event.params.exchangeKind' });
  const advancedExchangeMethod = watch('event.params.advancedExchangeMethod');

  const isUnevenExchangeEnabled =
    advancedExchangeMethod === 'PAYMENT' ||
    advancedExchangeMethod === 'PAYMENT_AND_PARTIAL_REFUND';

  const onExchangeKindChange = (kind: ExchangeKind) => {
    const shared = {
      advancedExchangeMethod,
      minInventoryAvailableToCustomer: getValues(
        'event.params.minInventoryAvailableToCustomer',
      ),
    };

    setValue(
      'event.params',
      kind === 'variant'
        ? {
            ...shared,
            exchangeKind: 'variant',
            advancedExchangeSortCriteria: 'ALPHABETICAL',
            excludedAdvancedExchangeOptionNames: ['Title'],
            // matches the variant exchange flow when no rule applies
            isAdvancedExchangeFilterAndSearchEnabled: false,
          }
        : {
            ...shared,
            exchangeKind: 'product',
            advancedExchangeSortCriteria: 'ALPHABETICAL',
            excludedAdvancedExchangeOptionNames: ['Title'],
            isAdvancedExchangeFilterAndSearchEnabled: true,
            storeProductGroupIds: [],
            storeProductTags: [],
          },
      { shouldDirty: true },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <SimpleSelect
        label="Exchange Type"
        details={`${exchangeKindDescription[exchangeKind]} If several exchange rules match an item, only the highest priority rule applies.`}
        options={exchangeKindOptions}
        onChange={onExchangeKindChange}
        value={exchangeKind}
      />

      {exchangeKind === 'product' && (
        <>
          <Controller
            control={control}
            name="event.params.storeProductGroupIds"
            render={({ field, fieldState }) => (
              <MultiSelect
                label="Product Groups"
                details="Products that belong to these groups will be offered for exchange."
                options={groups}
                value={groups.filter((group) =>
                  field.value.includes(Number.parseInt(group.value, 10)),
                )}
                onChange={(selections) =>
                  field.onChange(
                    selections.map(({ value }) => Number.parseInt(value, 10)),
                  )
                }
                error={fieldState.error?.message}
              />
            )}
          />

          <Controller
            control={control}
            name="event.params.storeProductTags"
            render={({ field, fieldState }) => (
              <MultiSelect
                creatable
                label="Product Tags"
                details="Products tagged with these tags will be offered for exchange."
                options={productTagOptions}
                value={(field.value ?? []).map((tag) => ({
                  label: tag,
                  value: tag,
                }))}
                onChange={(selections) =>
                  field.onChange(selections.map(({ value }) => value))
                }
                error={fieldState.error?.message}
              />
            )}
          />
          {selectedTags.length > 0 && (
            <Controller
              control={control}
              name="event.params.matchAllTags"
              render={({ field: f }) => (
                <SwitchInput
                  id="require-all-tags-to-exist"
                  label="Match All Tags"
                  details={
                    f.value
                      ? 'Only offer products that have every selected tag (AND).'
                      : 'Offer products that have at least one selected tag (OR).'
                  }
                  checked={f.value}
                  onChange={f.onChange}
                />
              )}
            />
          )}
        </>
      )}

      <Controller
        control={control}
        name="event.params.advancedExchangeMethod"
        render={({ field, fieldState }) => (
          <SimpleSelect
            disabled={!isStripeConnected}
            defaultValue="NO_PAYMENT_OR_REFUND"
            label="Exchange Method"
            details={field.value && advancedExchangeDescription[field.value]}
            options={advancedExchangeOptions}
            onChange={field.onChange}
            value={field.value}
            error={fieldState.error?.message}
          />
        )}
      />

      {exchangeKind === 'product' && isUnevenExchangeEnabled && (
        <Controller
          control={control}
          name="event.params.isNoAdjustmentVariantExchangeEnabled"
          render={({ field: f, fieldState }) => (
            <SwitchInput
              id="is-no-adjustment-variant-exchange-enabled"
              label="Allow Variant Exchange Without Adjustment"
              details="When enabled, customers exchanging a variant for a different variant will not be charged or refunded for any price adjustments."
              checked={f.value}
              onChange={f.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
      )}
      <Controller
        control={control}
        name="event.params.minInventoryAvailableToCustomer"
        render={({ field: { value, onChange }, fieldState }) => (
          <NumberInput
            id="min-inventory-available-to-customer"
            label="Minimum Inventory Available to Customer"
            details="Optionally override the default inventory available to customer setting."
            error={fieldState.error?.message}
            value={value}
            onChange={(e) => onChange(getNullableNumberValue(e))}
          />
        )}
      />

      {exchangeKind === 'product' && (
        <>
          <Controller
            control={control}
            name="event.params.isAdvancedExchangeFilterAndSearchEnabled"
            render={({ field: f }) => (
              <SwitchInput
                id="is-advanced-exchange-filter-and-search-enabled"
                label="Filter And Search"
                details="When enabled, customers search and filter products available for exchange."
                checked={f.value}
                onChange={f.onChange}
              />
            )}
          />

          {isFilterAndSearchEnabled && (
            <>
              <Controller
                control={control}
                name="event.params.excludedAdvancedExchangeOptionNames"
                render={({ field: f, fieldState }) => (
                  <MultiSelect
                    creatable
                    label="Excluded Options"
                    options={[]}
                    details="Options to exclude from the filter selection, such as 'Title' or 'Description'. Excluding an option from filtering, does not prevent it from being displayed in the search results. Option names must match exactly."
                    placeholder="Add option name to exclude"
                    value={
                      f.value?.map((v) => ({
                        value: v.toString(),
                        label: v,
                      })) ?? []
                    }
                    onChange={(o) => f.onChange(o.map((v) => v.value))}
                    error={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                control={control}
                name="event.params.advancedExchangeSortCriteria"
                render={({ field, fieldState }) => (
                  <SimpleSelect
                    label="Order Products By"
                    options={Object.entries(
                      advancedExchangeSortCriteriaLabel,
                    ).map(([key, label]) => ({
                      label,
                      value: key,
                    }))}
                    onChange={field.onChange}
                    value={field.value}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </>
          )}
        </>
      )}

      {!isStripeConnected && (
        <Alert
          variant="info"
          heading="To use a different exchange method (including the ability to charge customers for exchanges), you must connect your Stripe account."
        />
      )}
    </div>
  );
}
